import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const EventRecordOverview = () => (
  <Page
    title="Event Record Overview"
    intro="Opening an event from the calendar takes you into its record: event details and coordination tools at the top, the Event Medical Plan and Invoicing cards to the right, rows of tasks, notes, documents, participants, and client feedback beneath, and the Planning workspace tabs at the bottom."
  >
    <Section title="What's on the event record">
      <Bullets>
        <Bullet>
          The top panel shows the event title, dates, and location, with the{' '}
          <strong>status chip</strong>, an <strong>EMP approved</strong> or{' '}
          <strong>EMP not required</strong> chip where relevant, and the CQC
          exposure chip. Buttons here cover <strong>Edit Event</strong> and,
          from the three-dot menu, <strong>Duplicate Event</strong>.
        </Bullet>
        <Bullet>
          The <strong>Event Information</strong> card lists start/end, organiser
          (linked to the client record), event type, location, What3Words, and
          who created and last updated the event.
        </Bullet>
        <Bullet>
          Below that sits the <strong>Shifts</strong> panel, covered on the
          Shifts and Documents page.
        </Bullet>
        <Bullet>
          The right-hand column holds the <strong>Event Medical Plan</strong>{' '}
          card (with the Planning Progress bars until the EMP is approved) and{' '}
          <strong>Invoicing</strong>.
        </Bullet>
        <Bullet>
          Below that, a full-width row puts <strong>Tasks</strong> and{' '}
          <strong>Internal Notes</strong> side by side, followed by a row of{' '}
          <strong>Documents</strong>, <strong>Participants</strong>, and{' '}
          <strong>Client Feedback</strong>.
        </Bullet>
        <Bullet>
          The <strong>Planning workspace</strong> panel at the bottom contains
          the planning tabs — Planning Intelligence, Operational plan, Clinical
          &amp; EMP, Risk assessment, CQC Exposure, Post-event, and Costs.
        </Bullet>
      </Bullets>
      <Info title="Who can do this?">
        The full event record needs the <strong>Event Planning</strong> team
        (site administrators always have access). Other staff use the read-only
        view described below.
      </Info>
    </Section>

    <Section title="Saving your changes">
      <Warning title="One save bar for the whole record">
        Edits to the event details and planning tabs are not saved as you type.
        A sticky <strong>Unsaved changes</strong> bar appears at the bottom with{' '}
        <strong>Reset</strong> and <strong>Save Changes</strong> — remember to
        save before leaving the page. A few actions save immediately without the
        bar: status changes, EMP approval, and the draft-EMP visibility switch.
      </Warning>
    </Section>

    <Section title="Event status and cancelling">
      <Bullets>
        <Bullet>
          Click the status chip to move the event through its lifecycle:
          Potential, Interested, Quote Sent, Planning, Ready, Event Finished,
          Awaiting Payment, Completed — or Cancelled. Your organisation can
          customise the list and colours in Site Configuration.
        </Bullet>
        <Bullet>
          Choosing <strong>Cancelled</strong> opens a confirmation that requires
          a <strong>Reason for Cancelling</strong>. If staff are already
          assigned, their names are listed and their shifts are removed.
        </Bullet>
      </Bullets>
      <Warning title="Cancelling does not email staff">
        Cancelling an event does not automatically notify the assigned staff
        listed in the dialog — contact them yourself. You also can't cancel
        while shift applications are still pending; review those first.
      </Warning>
    </Section>

    <Section title="Notes, tasks, and messaging staff">
      <Bullets>
        <Bullet>
          <strong>Internal Notes</strong> are visible to event planning staff
          only. When adding a note you can tick{' '}
          <strong>Add to Command Log</strong> to copy it into the event's
          command log.
        </Bullet>
        <Bullet>
          The <strong>Tasks</strong> pane tracks planner actions and follow-ups
          tied to the event, using the standard AmbuNet tasks system.
        </Bullet>
        <Bullet>
          From the Shifts panel's three-dot menu you can{' '}
          <strong>SMS assigned staff</strong> (uses your SMS credits, with
          optional links to Available Shifts, announcements, and other AmbuNet
          pages), or <strong>Email assigned staff</strong> — this opens your
          own mail app with everyone BCC'd and copies the addresses to your
          clipboard.
        </Bullet>
        <Bullet>
          Every SMS sent from the event is recorded as an{' '}
          <strong>Internal Note</strong> naming the recipients and quoting the
          message, so the broadcast history lives with the event.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Client Feedback">
      <Bullets>
        <Bullet>
          The <strong>Client Feedback</strong> panel asks the event organiser
          how it went without them needing an AmbuNet account.{' '}
          <strong>Request feedback</strong> creates a private, single-use link
          to a short form (star ratings and a couple of comment boxes) — pick
          a client contact or type a name, add an optional personal message,
          and either email the link from AmbuNet or copy it into your own
          email, text, or WhatsApp.
        </Bullet>
        <Bullet>
          Each link expires after <strong>14, 30, 60, or 90 days</strong> (30
          is the default) and can be used once. Rows show each request's
          status — <strong>Awaiting feedback</strong>,{' '}
          <strong>Received</strong>, <strong>Expired</strong>, or{' '}
          <strong>Cancelled</strong> — and open links can be copied, re-sent
          as a reminder, or cancelled. An expired link nobody cancelled can be
          reopened and emailed again.
        </Bullet>
        <Bullet>
          Received responses expand in the panel with their ratings and
          comments, and the header shows the average score. A text copy of
          each response is also appended to the{' '}
          <strong>Client Feedback</strong> field on the Post-event tab, so it
          feeds the post-event analysis — see{' '}
          <DocLink
            docsPage
            url="/event-planning/post-event-analysis"
            text="Post-Event Analysis"
          />
          .
        </Bullet>
      </Bullets>
      <Info title="Who can do this?">
        Creating, resending, and cancelling feedback links needs the{' '}
        <strong>Event Planning</strong> team. The form itself is public to
        whoever holds the link — it shows only the event's name and dates.
      </Info>
    </Section>

    <Section title="Duplicating an event">
      <Bullets>
        <Bullet>
          <strong>Duplicate Event</strong> (three-dot menu) copies the event
          with a new name, dates, organisation, type, and location. You choose
          which shifts to copy — their times, role, rate, and bookable flag come
          across, but never the assigned staff.
        </Bullet>
        <Bullet>
          <strong>Change dates of all shifts</strong> in the dialog shifts every
          copied shift onto the new event dates in one go.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="The read-only event view">
      <p>
        Staff outside the Event Planning team see a simplified, read-only event
        information page instead of the full record. It shows the event details,
        the approved Event Medical Plan, documents marked visible to all staff,
        the shift list (with contact details for assigned staff), and any
        vehicles booked for the event.
      </p>
      <Info title="How staff get there">
        The calendar sends non-planners there automatically, the{' '}
        <strong>View Event Info</strong> button on a shift application opens it,
        and the "Email EMP to All Staff" action sends a link to it. Any
        signed-in user can open it.
      </Info>
    </Section>

    <Screenshot url="events/event-record.png">
      An event record showing the top panel, the EMP and Invoicing column,
      Tasks and Notes, and the Documents, Participants, and Client Feedback
      row.
    </Screenshot>
  </Page>
)

export default EventRecordOverview
